import { useEffect, useRef, useState } from 'react';

/** Frames in the assembly clip (0 = scattered cluster … 76 = formed peak). */
const TOTAL_FRAMES = 77;

/**
 * Ease rate (per second). 1 - e^(-rate·dt) per tick → settles a full-range
 * move in ~0.3s. Keep this high: a slow ease makes the frame-quantised tail
 * step one frame at a time at a slowing cadence, which reads as stutter.
 */
const EASE_RATE = 16;

/** Snap to target once within half a source frame — kills the asymptotic tail. */
const SNAP_EPSILON = 0.5 / (TOTAL_FRAMES - 1);

/**
 * HeroCube — a forming-cube assembly video on the hero's right, scrubbed by the
 * cursor. The cube's assembly state maps to the cursor's distance from it:
 * closer → more formed, further → scattered. The position is eased with a
 * short time-based ease (~0.3s settle, snap-to-target at the end) so motion is
 * smooth while moving but stops crisply with the cursor. It tracks the cursor
 * finely (small mouse moves → small cube moves) across a wide area.
 *
 * There is NO autonomous motion — when the cursor is still, the cube is still.
 * It only moves in response to the cursor moving. Seeking is frame-quantised so
 * we only move the video when the target crosses into a new source frame.
 *
 * A CRT overlay (scanlines + vignette + roll + faint flicker) sits on top,
 * inside the masked container so it fades at the edges with the video.
 *
 * Static fallback (prefers-reduced-motion or coarse/touch pointers): a still
 * poster of the formed peak. Hidden below `lg`. Decorative — aria-hidden.
 */
export default function HeroCube() {
  const [isStatic, setIsStatic] = useState(true);
  const videoRef = useRef(null);
  const rafRef = useRef(0);
  const st = useRef({ cur: 0, mx: null, my: null, lastFrame: -1 });

  // Decide static vs interactive from motion + pointer preferences.
  useEffect(() => {
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const coarse = window.matchMedia('(pointer: coarse)');
    const update = () => setIsStatic(motion.matches || coarse.matches);
    update();
    motion.addEventListener('change', update);
    coarse.addEventListener('change', update);
    return () => {
      motion.removeEventListener('change', update);
      coarse.removeEventListener('change', update);
    };
  }, []);

  // Cursor-driven scrub loop.
  useEffect(() => {
    if (isStatic) return;
    const video = videoRef.current;
    if (!video) return;

    const s = st.current;
    s.cur = 0;
    s.mx = null;
    s.my = null;
    s.lastFrame = -1;
    s.t = 0;
    let duration = 0;

    const onMove = (e) => {
      s.mx = e.clientX;
      s.my = e.clientY;
    };
    window.addEventListener('pointermove', onMove, { passive: true });

    const tick = (now) => {
      const rect = video.getBoundingClientRect();
      // Cube body sits center-right within the frame.
      const cx = rect.left + rect.width * 0.58;
      const cy = rect.top + rect.height * 0.5;

      // Target assembly (0 = scattered … 1 = formed) from cursor distance.
      // Wide radius → fine increments and a broad range as the cursor roams.
      let target = 0;
      if (s.mx != null) {
        const dist = Math.hypot(s.mx - cx, s.my - cy);
        const maxD = Math.max(840, rect.width * 0.9);
        target = Math.min(1, Math.max(0, 1 - dist / maxD));
      }

      // Time-based ease toward the cursor-derived target so settle speed is
      // refresh-rate independent, then snap once within half a frame so the
      // cube stops dead with the cursor instead of coasting frame by frame.
      const dt = Math.min((now - (s.t || now)) / 1000, 0.1);
      s.t = now;
      const diff = target - s.cur;
      s.cur = Math.abs(diff) < SNAP_EPSILON
        ? target
        : s.cur + diff * (1 - Math.exp(-EASE_RATE * dt));

      // Frame-quantised seek: only move when we cross into a new frame.
      if (duration > 0) {
        const frame = Math.round(s.cur * (TOTAL_FRAMES - 1));
        if (frame !== s.lastFrame) {
          s.lastFrame = frame;
          try {
            video.currentTime = ((frame + 0.5) * duration) / TOTAL_FRAMES;
          } catch {
            /* seek can throw before the media is seekable; ignore */
          }
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    const start = () => {
      duration = video.duration || 0;
      // Prime the decoder so seeked frames actually paint, then hold at scatter.
      video.play().then(() => video.pause()).catch(() => {});
      video.currentTime = 0;
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(tick);
    };

    if (video.readyState >= 2) start();
    else video.addEventListener('loadeddata', start, { once: true });

    return () => {
      window.removeEventListener('pointermove', onMove);
      video.removeEventListener('loadeddata', start);
      cancelAnimationFrame(rafRef.current);
    };
  }, [isStatic]);

  return (
    <div className="hero-cube hidden lg:block" aria-hidden="true">
      {isStatic ? (
        <img
          src="/video/hero-cube-poster.webp"
          alt=""
          className="hero-cube__media"
          width="1280"
          height="720"
        />
      ) : (
        <video
          ref={videoRef}
          className="hero-cube__media"
          muted
          playsInline
          preload="auto"
          poster="/video/hero-cube-scatter.webp"
          width="1280"
          height="720"
        >
          <source src="/video/hero-cube.webm" type="video/webm" />
          <source src="/video/hero-cube.mp4" type="video/mp4" />
        </video>
      )}
      <div className="hero-cube__crt" />
    </div>
  );
}
